import React from 'react';

import { useAtomValue } from 'jotai';

import { cssMap, cx } from '@atlaskit/css';
import CurlyBracketsIcon from '@atlaskit/icon/core/curly-brackets';
import TableIcon from '@atlaskit/icon/core/table';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle/toggle';
import { token } from '@atlaskit/tokens';

import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { QueryWorkbenchTopNavigation } from '../../../components/QueryWorkbench';
import { QueryBuilderIsDebugEnabledAtom } from '../../../state/QueryBuilderAtoms';
import { QueryAssistantButton } from '../home/QueryAssistantButton';
import type { QueryBuilderColorMode } from '../theme';
import type { QueryBuilderView } from '../types';

const styles = cssMap({
	sidebar: {
		height: 'auto',
		minWidth: '0px',
		display: 'flex',
		flexDirection: 'column',
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.250'),
		paddingInline: token('space.150'),
		'@media (min-width: 48rem)': {
			height: '100%',
			borderInlineEndWidth: token('border.width'),
			borderInlineEndStyle: 'solid',
			borderInlineEndColor: token('color.border'),
			borderBlockEndWidth: 0,
		},
	},
	sidebarToggle: {
		marginBlockStart: 'auto',
		paddingBlockStart: token('space.300'),
		paddingInline: token('space.150'),
	},
	navItem: {
		width: '100%',
		minHeight: '44px',
		display: 'flex',
		alignItems: 'center',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		borderRadius: token('radius.full'),
		backgroundColor: 'transparent',
		color: token('color.text.subtle'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		textAlign: 'start',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text'),
			textDecoration: 'none',
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	navItemSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
	navText: {
		font: token('font.body'),
		fontWeight: token('font.weight.semibold'),
	},
	themeToggle: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

type QueryBuilderNavigationProps = {
	activeView: QueryBuilderView;
	onNavigate: (path: string) => void;
	showThemeToggle?: boolean;
	onEnableDarkMode?: () => void;
};

type QueryBuilderNavigationItem = {
	id: 'build-query' | 'saved-queries';
	label: string;
	icon: typeof CurlyBracketsIcon;
	href: string;
	selected: (view: QueryBuilderView) => boolean;
};

const items: readonly QueryBuilderNavigationItem[] = [
	{
		id: 'build-query',
		label: 'Build query',
		icon: CurlyBracketsIcon,
		href: '/query-builder',
		selected: (view) =>
			view === 'builder' || view === 'saved-query-edit' || view === 'saved-query-test',
	},
	{
		id: 'saved-queries',
		label: 'Saved queries',
		icon: TableIcon,
		href: '/query-builder/saved-queries',
		selected: (view) => view === 'saved-queries' || view === 'saved-query-details',
	},
];

export const QueryBuilderNavigation = ({
	activeView,
	onNavigate,
	showThemeToggle = false,
	onEnableDarkMode,
}: QueryBuilderNavigationProps): React.JSX.Element => {
	return (
		<Box as="nav" xcss={styles.sidebar} aria-label="Query Builder navigation">
			<Stack space="space.100">
				{items.map((item) => {
					const Icon = item.icon;
					const isSelected = item.selected(activeView);

					return (
						<Anchor
							key={item.id}
							href={item.href}
							aria-current={isSelected ? 'page' : undefined}
							xcss={cx(styles.navItem, isSelected && styles.navItemSelected)}
							onClick={(event) => {
								if (
									event.button !== 0 ||
									event.metaKey ||
									event.ctrlKey ||
									event.shiftKey ||
									event.altKey
								) {
									return;
								}
								event.preventDefault();
								onNavigate(item.href);
							}}
						>
							<Inline space="space.150" alignBlock="center">
								<Icon label="" color="currentColor" />
								<Box xcss={styles.navText}>{item.label}</Box>
							</Inline>
						</Anchor>
					);
				})}
			</Stack>
			{showThemeToggle ? (
				<Box xcss={styles.sidebarToggle}>
					<Box xcss={styles.themeToggle}>
						<Toggle
							id="query-builder-dark-mode"
							label="Dark mode"
							isChecked={false}
							onChange={() => onEnableDarkMode?.()}
						/>
						<Text as="span">Dark mode</Text>
					</Box>
				</Box>
			) : null}
		</Box>
	);
};

type QueryBuilderTopNavigationProps = {
	activeView: QueryBuilderView;
	colorMode: QueryBuilderColorMode;
	onNavigate: (path: string) => void;
	onColorModeChange: (colorMode: QueryBuilderColorMode) => void;
};

export const QueryBuilderTopNavigation = ({
	activeView,
	colorMode,
	onNavigate,
	onColorModeChange,
}: QueryBuilderTopNavigationProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	const debugEnabled = useAtomValue(QueryBuilderIsDebugEnabledAtom);
	const isDark = colorMode === 'dark';
	const colorModeLabel = isDark ? 'Light mode' : 'Dark mode';

	return (
		<QueryWorkbenchTopNavigation
			homeHref="/query-builder"
			homeLabel="Query Builder home"
			navigationLabel="Query Builder navigation"
			onNavigate={(path) => {
				if (!debugEnabled) {
					fireEvent('track.queryBuilder.navigationClicked', {
						destination: path === '/query-builder/saved-queries' ? 'saved-queries' : 'builder',
					});
				}
				onNavigate(path);
			}}
			items={items.map((item) => ({
				id: item.id,
				label: item.label,
				href: item.href,
				isSelected: item.selected(activeView),
			}))}
			endContent={
				<Box xcss={styles.themeToggle}>
					{activeView !== 'saved-queries' && activeView !== 'saved-query-details' ? (
						<QueryAssistantButton />
					) : null}
					<Text as="span">{colorModeLabel}</Text>
					<Toggle
						id="query-builder-color-mode"
						label={colorModeLabel}
						isChecked={false}
						onChange={() => onColorModeChange(isDark ? 'light' : 'dark')}
					/>
				</Box>
			}
		/>
	);
};
